// Modern ERP Dashboard - Replacing the control panel home page
// Comprehensive business overview with key metrics and quick actions

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FeatureModule } from './ControlPanel';
import OptinodeAILogo from '../common/OptinodeAILogo';
import { 
  DashboardIcon, 
  AnalyticsIcon, 
  PackageIcon, 
  TrendUpIcon, 
  AlertTriangleIcon, 
  CheckCircleIcon, 
  BuildingIcon,
  WarehouseIcon,
  ZapIcon,
  TargetIcon,
  RefreshIcon,
  ChartBarIcon
} from '../common/icons';
import './ERPDashboard.css';

interface KPICard {
  id: string;
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down' | 'stable';
  status: 'healthy' | 'warning' | 'critical';
  icon: React.ComponentType<{ width?: number; height?: number; color?: string; className?: string }>;
  description: string;
}

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ width?: number; height?: number; color?: string; className?: string }>;
  route: FeatureModule;
  color: string;
}

interface RecentActivity {
  id: string;
  type: 'optimization' | 'inventory' | 'alert' | 'system';
  title: string;
  description: string;
  timestamp: string;
  status: 'success' | 'warning' | 'error' | 'info';
}

export const ERPDashboard: React.FC<{ onModuleChange?: (module: FeatureModule) => void }> = ({ onModuleChange }) => {
  const { state: authState } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [kpiData, setKpiData] = useState<KPICard[]>([]);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Update time every minute
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Mock KPI data
      const mockKPIs: KPICard[] = [
        {
          id: 'revenue',
          title: 'Monthly Revenue',
          value: '$847K',
          change: '+12.5%',
          trend: 'up',
          status: 'healthy',
          icon: TrendUpIcon,
          description: 'Revenue growth vs last month'
        },
        {
          id: 'inventory-value',
          title: 'Inventory Value',
          value: '$2.4M',
          change: '+8.2%',
          trend: 'up',
          status: 'healthy',
          icon: PackageIcon,
          description: 'Total inventory across all locations'
        },
        {
          id: 'active-orders',
          title: 'Active Orders',
          value: 1247,
          change: '+15.3%',
          trend: 'up',
          status: 'healthy',
          icon: ChartBarIcon,
          description: 'Orders in processing pipeline'
        },
        {
          id: 'warehouse-efficiency',
          title: 'Warehouse Efficiency',
          value: '94.2%',
          change: '+2.1%',
          trend: 'up',
          status: 'healthy',
          icon: WarehouseIcon,
          description: 'Overall operational efficiency'
        },
        {
          id: 'customer-satisfaction',
          title: 'Customer Satisfaction',
          value: '4.8/5',
          change: '+0.2',
          trend: 'up',
          status: 'healthy',
          icon: TargetIcon,
          description: 'Average customer rating'
        },
        {
          id: 'system-uptime',
          title: 'System Uptime',
          value: '99.9%',
          change: 'Stable',
          trend: 'stable',
          status: 'healthy',
          icon: ZapIcon,
          description: 'System availability this month'
        }
      ];

      // Mock Quick Actions
      const mockQuickActions: QuickAction[] = [
        {
          id: 'inventory-management',
          title: 'Inventory Management',
          description: 'View and manage inventory levels across all locations',
          icon: PackageIcon,
          route: 'inventory',
          color: 'var(--color-primary)'
        },
        {
          id: 'warehouse-network',
          title: 'Warehouse Network',
          description: 'Monitor and configure warehouse nodes',
          icon: BuildingIcon,
          route: 'network',
          color: 'var(--color-secondary)'
        },
        {
          id: 'optimization-engine',
          title: 'Run Optimization',
          description: 'Optimize inventory allocation and distribution',
          icon: ZapIcon,
          route: 'optimize',
          color: 'var(--color-success)'
        },
        {
          id: 'analytics-reports',
          title: 'Analytics & Reports',
          description: 'View detailed analytics and generate reports',
          icon: AnalyticsIcon,
          route: 'dashboard',
          color: 'var(--color-warning)'
        },
        {
          id: 'sop-planning',
          title: 'S&OP Planning',
          description: 'Sales and operations planning tools',
          icon: TargetIcon,
          route: 'planning',
          color: 'var(--color-error)'
        },
        {
          id: 'system-settings',
          title: 'System Settings',
          description: 'Configure system preferences and settings',
          icon: DashboardIcon,
          route: 'dashboard',
          color: 'var(--color-neutral-600)'
        }
      ];

      // Mock Recent Activities
      const mockActivities: RecentActivity[] = [
        {
          id: '1',
          type: 'optimization',
          title: 'Optimization Complete',
          description: 'Inventory allocation optimized for 24 warehouse locations',
          timestamp: '2 minutes ago',
          status: 'success'
        },
        {
          id: '2',
          type: 'inventory',
          title: 'Low Stock Alert',
          description: 'Product SKU-12345 is running low in Warehouse A',
          timestamp: '15 minutes ago',
          status: 'warning'
        },
        {
          id: '3',
          type: 'system',
          title: 'System Update',
          description: 'ERP system updated to version 2.1.4',
          timestamp: '1 hour ago',
          status: 'info'
        },
        {
          id: '4',
          type: 'alert',
          title: 'Demand Spike Detected',
          description: 'Unusual demand pattern detected for electronics category',
          timestamp: '2 hours ago',
          status: 'warning'
        }
      ];

      setKpiData(mockKPIs);
      setQuickActions(mockQuickActions);
      setRecentActivities(mockActivities);
      setIsLoading(false);
    };

    loadDashboardData();
  }, []);

  const getStatusIcon = (status: 'healthy' | 'warning' | 'critical') => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon width={20} height={20} color="var(--color-success)" />;
      case 'warning':
        return <AlertTriangleIcon width={20} height={20} color="var(--color-warning)" />;
      case 'critical':
        return <AlertTriangleIcon width={20} height={20} color="var(--color-error)" />;
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return '↗';
      case 'down':
        return '↘';
      case 'stable':
        return '→';
    }
  };

  const handleQuickActionClick = (route: FeatureModule) => {
    if (onModuleChange) {
      onModuleChange(route);
    }
  };

  const getActivityIcon = (type: RecentActivity['type']) => {
    switch (type) {
      case 'optimization':
        return <ZapIcon width={16} height={16} color="var(--color-success)" />;
      case 'inventory':
        return <PackageIcon width={16} height={16} color="var(--color-warning)" />;
      case 'alert':
        return <AlertTriangleIcon width={16} height={16} color="var(--color-error)" />;
      case 'system':
        return <DashboardIcon width={16} height={16} color="var(--color-primary)" />;
    }
  };

  if (isLoading) {
    return (
      <div className="erp-dashboard__loading">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="erp-dashboard">
      {/* Dashboard Header */}
      <div className="erp-dashboard__header">
        <div className="header__content">
          <div className="header__welcome">
            <div className="header__brand">
              <OptinodeAILogo width={48} height={48} className="header__logo" />
              <div className="header__brand-text">
                <h1 className="header__title">
                  Good {currentTime.getHours() < 12 ? 'Morning' : currentTime.getHours() < 18 ? 'Afternoon' : 'Evening'}, {authState.user?.profile?.firstName || 'User'}
                </h1>
                <p className="header__subtitle">
                  Here's what's happening with your business today
                </p>
              </div>
            </div>
          </div>
          <div className="header__meta">
            <div className="header__time">
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <button className="header__refresh" onClick={() => window.location.reload()}>
              <RefreshIcon width={20} height={20} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="erp-dashboard__kpis">
        <h2 className="section__title">Key Performance Indicators</h2>
        <div className="kpi-grid">
          {kpiData.map((kpi) => (
            <div key={kpi.id} className={`kpi-card kpi-card--${kpi.status}`}>
              <div className="kpi-card__header">
                <div className="kpi-card__icon">
                  <kpi.icon width={28} height={28} color="currentColor" />
                </div>
                <div className="kpi-card__status">
                  {getStatusIcon(kpi.status)}
                </div>
              </div>
              <div className="kpi-card__content">
                <h3 className="kpi-card__title">{kpi.title}</h3>
                <div className="kpi-card__value">{kpi.value}</div>
                <div className={`kpi-card__change kpi-card__change--${kpi.trend}`}>
                  <span className="change__icon">{getTrendIcon(kpi.trend)}</span>
                  <span className="change__value">{kpi.change}</span>
                </div>
                <p className="kpi-card__description">{kpi.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="erp-dashboard__main">
        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2 className="section__title">Quick Actions</h2>
          <div className="quick-actions-grid">
            {quickActions.map((action) => (
              <button 
                key={action.id} 
                className="quick-action-card"
                onClick={() => handleQuickActionClick(action.route)}
              >
                <div className="quick-action__icon" style={{ color: action.color }}>
                  <action.icon width={32} height={32} color="currentColor" />
                </div>
                <div className="quick-action__content">
                  <h3 className="quick-action__title">{action.title}</h3>
                  <p className="quick-action__description">{action.description}</p>
                </div>
                <div className="quick-action__arrow">→</div>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="dashboard-section">
          <h2 className="section__title">Recent Activities</h2>
          <div className="activities-list">
            {recentActivities.map((activity) => (
              <div key={activity.id} className={`activity-item activity-item--${activity.status}`}>
                <div className="activity__icon">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="activity__content">
                  <h4 className="activity__title">{activity.title}</h4>
                  <p className="activity__description">{activity.description}</p>
                  <span className="activity__timestamp">{activity.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
          <button className="activities__view-all">
            View All Activities →
          </button>
        </div>
      </div>

      {/* System Status Bar */}
      <div className="erp-dashboard__status-bar">
        <div className="status-bar__item">
          <div className="status-indicator status-indicator--healthy"></div>
          <span>All Systems Operational</span>
        </div>
        <div className="status-bar__item">
          <span>Last Updated: {currentTime.toLocaleTimeString()}</span>
        </div>
        <div className="status-bar__item">
          <span>Version 2.1.4</span>
        </div>
      </div>
    </div>
  );
};

export default ERPDashboard;